import { useState } from 'react'
import { api, type State } from '../api'
import type { Ruleset } from '../../shared/scoring'

/*
 * Setup - the commissioner's screen.
 *
 * The lock on this screen is the visible half of a real one: every route under
 * /api/admin/ checks the admin token on the server, so hiding the tab is a
 * convenience and not the security.
 *
 * Two different kinds of knob live here and they are deliberately kept apart:
 * the WEIGHTS decide which games make the board and belong to Scott; the RULES
 * decide what a correct pick is worth and belong to the boys. They share the
 * word "score" and nothing else.
 */


export function Setup({ state, onChanged }: { state: State; onChanged: () => void }) {
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)


  const refresh = async () => {
    if (!state.week) return
    setBusy(true)
    try {
      const r = await api.admin.refresh(state.week.id)
      setMsg(`Updated ${r.updated} rows from ESPN.`)
      onChanged()
    } finally { setBusy(false) }
  }

  const setRule = async (patch: Parameters<typeof api.admin.rules>[0]) => {
    await api.admin.rules(patch)
    onChanged()
  }

  const btn = 'slate-tap w-full px-4 py-3 text-base font-bold uppercase tracking-[0.04em]'

  return (
    <div className="px-4 py-5">
      {/*
       * BOARD CREATION LIVED HERE TWICE.
       *
       * This screen predates the Admin Board screen and kept its own copy of
       * the form - a label, a week number, and two datetime pickers whose
       * first kickoff defaulted to NOW, so it offered a board opening at 8:11
       * on a Saturday evening. Both were rendered on the same tab at the same
       * moment, so the copy that had been fixed sat directly above the copy
       * that had not.
       *
       * A board is started on the Board tab now, and nothing is typed.
       */}
      {msg && <p className="mt-3 text-sm">{msg}</p>}

      {state.week && (
        <>
          <h2 className="slate-num mt-8 mb-3 text-[10px] uppercase tracking-[0.12em] text-mute">
            Tell everyone
          </h2>
          {/*
           * /w/<weekId> is a public page carrying only the week label and a
           * game count, so a messaging app can render a preview card without
           * being let into the pool. It shows no roster and no picks - a link
           * preview is the easiest place in the whole app to leak a board by
           * accident.
           */}
          <button
            type="button"
            onClick={async () => {
              const link = `${location.origin}/w/${state.week!.id}`
              try {
                await navigator.clipboard.writeText(link)
                setMsg('Link copied. Paste it into the family text.')
              } catch {
                setMsg(link)
              }
            }}
            className={`${btn} border border-hairline`}
          >
            Copy this week’s link
          </button>

          <h2 className="slate-num mt-8 mb-3 text-[10px] uppercase tracking-[0.12em] text-mute">
            Results
          </h2>
          <button type="button" disabled={busy} onClick={() => void refresh()} className={`${btn} border border-hairline`}>
            Fetch scores for {state.week.label}
          </button>
        </>
      )}

      <h2 className="slate-num mt-8 mb-1 text-[10px] uppercase tracking-[0.12em] text-mute">
        The rules
      </h2>
      <p className="mb-3 text-xs text-mute">
        These are the boys’ to argue over. Changing one re-scores every week already played.
      </p>

      <div className="grid gap-3">
        <label className="flex items-center justify-between gap-3 border border-hairline px-3 py-3">
          <span className="text-base">Underdog bonus</span>
          <select
            value={state.rules.underdogBonus}
            onChange={(e) => void setRule({ underdogBonus: Number(e.target.value) })}
            className="slate-num bg-ground px-2 py-2"
          >
            <option value={0}>off</option>
            <option value={1}>+1</option>
            <option value={2}>+2</option>
            <option value={3}>+3</option>
          </select>
        </label>

        <label className="flex items-center justify-between gap-3 border border-hairline px-3 py-3">
          <span className="text-base">If someone forgets</span>
          <select
            value={state.rules.missingPick}
            onChange={(e) =>
              void setRule({ missingPick: e.target.value as Ruleset['missingPick'] })
            }
            className="slate-num bg-ground px-2 py-2"
          >
            <option value="favorite">take the favorite</option>
            <option value="zero">score zero</option>
            {/* The favourites, then capped at the worst player who did pick.
                Never beats somebody who turned up; can tie them. */}
            <option value="lowest">favorites, but never above last</option>
          </select>
        </label>

        <label className="flex items-center justify-between gap-3 border border-hairline px-3 py-3">
          <span className="text-base">A tie</span>
          <select
            value={state.rules.tieCredits}
            onChange={(e) => void setRule({ tieCredits: e.target.value as 'both' | 'neither' })}
            className="slate-num bg-ground px-2 py-2"
          >
            <option value="both">pays both</option>
            <option value="neither">pays neither</option>
          </select>
        </label>
      </div>

      <h2 className="slate-num mt-8 mb-3 text-[10px] uppercase tracking-[0.12em] text-mute">PINs</h2>
      <p className="mb-3 text-xs text-mute">
        Nobody needs one. Set one and only that person can enter their own picks.
      </p>
      <ul className="grid gap-2">
        {state.players.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 border border-hairline px-3 py-3">
            <span className="text-base font-bold uppercase">{p.name}</span>
            <PinControl playerId={p.id} hasPin={p.has_pin === 1} onDone={onChanged} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function PinControl({ playerId, hasPin, onDone }: { playerId: string; hasPin: boolean; onDone: () => void }) {
  const [v, setV] = useState('')
  return (
    <span className="flex items-center gap-2">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        inputMode="numeric"
        placeholder={hasPin ? 'set' : 'none'}
        aria-label={`PIN for ${playerId}`}
        className="slate-num w-20 border border-hairline bg-ground px-2 py-2 text-center"
      />
      <button
        type="button"
        onClick={async () => { await api.admin.pin(playerId, v || null); setV(''); onDone() }}
        className="slate-num slate-tap px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-gold-text"
      >
        {v ? 'Set' : 'Clear'}
      </button>
    </span>
  )
}
